import React from "react";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Popover from "@mui/material/Popover";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import InputBase from "@mui/material/InputBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import SearchIcon from "@mui/icons-material/Search";
import CodeIcon from "@mui/icons-material/Code";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HighlightAltIcon from "@mui/icons-material/HighlightAlt";
import NearMeIcon from "@mui/icons-material/NearMe";
import DeselectIcon from "@mui/icons-material/Deselect";

import Graph from "graphology";
import forceAtlas2 from "graphology-layout-forceatlas2";

import useActions, { useAppDispatch, useAppSelector } from "../../app/hooks";
import RepoFeatureView from "./RepoFeatureView";
import { selectUser } from "../auth/authSlice";
import { setNavLocation, resetNavLocation } from "../navigation/navigationSlice";
import { BookmarkMenu } from "../headermenu/BookmarkMenu";
import { apiAnchorMethod, apiLaunchCandidates } from "../../services/StructuresAPI";
import {
  NetworkNode,
  NetworkPov,
  NetworkMode,
  closeNetwork,
  closeNetworkCode,
  collapseAllNetwork,
  collapseNetworkFile,
  enterNetworkFocus,
  exitNetworkFocus,
  selectIndexStatus,
  selectNetworkCodeBody,
  selectNetworkCodeOpen,
  selectNetworkCodeStatus,
  selectNetworkCodeSubtitle,
  selectNetworkCodeTitle,
  selectNetworkEdges,
  selectNetworkError,
  selectNetworkFocusFile,
  selectNetworkHiddenDirs,
  selectNetworkNodes,
  selectNetworkOpen,
  selectNetworkPov,
  selectNetworkMode,
  selectNetworkRepoId,
  selectNetworkSelected,
  selectNetworkMultiSelected,
  selectNetworkNodeColors,
  selectNetworkStatus,
  setNetworkHiddenDirs,
  setNetworkPov,
  setNetworkMode,
  setNetworkSelected,
  toggleNetworkSelected,
  setNetworkMultiSelected,
  clearNetworkSelected,
  setNetworkNodeColors,
  clearNetworkNodeColors,
} from "./librarySlice";

// sigma@3's shipped .d.ts uses TS 4.7+ "instantiation expression" syntax that
// this project's TypeScript (4.1) can't parse. Load the runtime via require so
// the type declarations are never pulled in, and treat the class as `any`.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const SigmaModule: any = require("sigma");
const Sigma: any = SigmaModule.default || SigmaModule;

// Node colours. Files blue, functions green; selected amber.
const COLOR_FILE = "#4389f5";
const COLOR_FUNCTION = "#4db151";
const COLOR_SELECTED = "#f59e0a";
const COLOR_EDGE = "#5a6488";

// Palette for "colour the selected group" (toolbar swatches).
const GROUP_COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#3b82f6", "#8b5cf6", "#ec4899", "#94a3b8"];

// Custom hover label: sigma's default draws a WHITE box with the (light) label
// colour → white-on-white. This draws a dark backing box with light text so the
// label is always readable when you hover/select a node.
function drawNodeHover(context: any, data: any, settings: any) {
  const size = settings.labelSize || 12;
  const font = settings.labelFont || "monospace";
  const weight = settings.labelWeight || "normal";

  // Emphasised node.
  context.fillStyle = data.color || "#fff";
  context.beginPath();
  context.arc(data.x, data.y, data.size + 1, 0, Math.PI * 2);
  context.fill();

  const label = data.label;
  if (typeof label !== "string" || !label) return;

  context.font = `${weight} ${size}px ${font}`;
  const tw = context.measureText(label).width;
  const padX = 5;
  const padY = 3;
  const lx = data.x + data.size + 4;
  const ly = data.y;

  // White box + BLACK text → always readable, clearly black.
  context.fillStyle = "#ffffff";
  context.beginPath();
  context.rect(lx - padX, ly - size / 2 - padY, tw + padX * 2, size + padY * 2);
  context.fill();

  context.fillStyle = "#000000";
  context.textAlign = "left";
  context.textBaseline = "middle";
  context.fillText(label, lx, ly);
}

// Normal label renderer. Non-selected nodes use the light label on the dark
// canvas; the SELECTED node (data.sel, set by the node reducer) gets a white box
// with BLACK text so its title is clearly readable when clicked.
function drawNodeLabel(context: any, data: any, settings: any) {
  const label = data.label;
  if (typeof label !== "string" || !label) return;
  const size = settings.labelSize || 12;
  context.font = `${settings.labelWeight || "normal"} ${size}px ${settings.labelFont || "monospace"}`;
  const lx = data.x + data.size + 4;
  const ly = data.y;

  if (data.sel) {
    const tw = context.measureText(label).width;
    const padX = 5;
    const padY = 3;
    context.fillStyle = "#ffffff";
    context.fillRect(lx - padX, ly - size / 2 - padY, tw + padX * 2, size + padY * 2);
    context.fillStyle = "#000000";
  } else {
    context.fillStyle = (settings.labelColor && settings.labelColor.color) || "#dbe6ff";
  }
  context.textAlign = "left";
  context.textBaseline = "middle";
  context.fillText(label, lx, ly);
}

type LayoutKind = "force" | "hierarchy";

// Run a layout over the WHOLE graphology graph, writing x/y. Sigma renders the
// resulting STATIC positions (no live simulation → no drift). forceAtlas2 needs
// initial positions, which we always set when adding nodes.
function runGraphLayout(graph: Graph, kind: LayoutKind) {
  if (graph.order === 0) return;
  if (kind === "hierarchy") {
    layoutHierarchy(graph);
    return;
  }
  const settings = forceAtlas2.inferSettings(graph);
  const iterations = graph.order > 3000 ? 120 : graph.order > 800 ? 250 : 400;
  forceAtlas2.assign(graph, { iterations, settings });
}

// Simple directed BFS layering from roots (no incoming edges) — a top-down call
// tree that pairs with the "altitude" POV.
function layoutHierarchy(graph: Graph) {
  const level = new Map<string, number>();
  const roots: string[] = [];
  graph.forEachNode((n) => { if (graph.inDegree(n) === 0) roots.push(n); });
  const seeds = roots.length ? roots : graph.nodes().slice(0, 1);
  const queue: string[] = [];
  seeds.forEach((n) => { level.set(n, 0); queue.push(n); });
  let head = 0;
  while (head < queue.length) {
    const n = queue[head++];
    const d = level.get(n)!;
    graph.forEachOutNeighbor(n, (m) => {
      if (!level.has(m)) { level.set(m, d + 1); queue.push(m); }
    });
  }
  // Unreached nodes (cycles / disconnected) drop to the bottom row.
  let maxLevel = 0;
  level.forEach((v) => { if (v > maxLevel) maxLevel = v; });
  graph.forEachNode((n) => { if (!level.has(n)) level.set(n, maxLevel + 1); });
  const perRow = new Map<number, number>();
  graph.forEachNode((n) => {
    const d = level.get(n)!;
    const col = perRow.get(d) ?? 0;
    perRow.set(d, col + 1);
    graph.setNodeAttribute(n, "x", col * 18);
    graph.setNodeAttribute(n, "y", -d * 60);
  });
}

// Is this path under any hidden directory prefix?
function isHidden(path: string | null | undefined, hidden: string[]): boolean {
  if (!path || hidden.length === 0) return false;
  return hidden.some((d) => path === d || path.startsWith(d + "/"));
}

// A directory in the nested filter tree.
interface DirNode {
  path: string;     // full prefix, e.g. "src/vendor"
  name: string;     // last segment, e.g. "vendor"
  count: number;    // files under it (recursive)
  children: DirNode[];
}

// Build the directory tree (all depths) from the file nodes' paths.
function buildDirTree(filePaths: string[]): DirNode {
  const root: DirNode = { path: "", name: "", count: 0, children: [] };
  const byPath = new Map<string, DirNode>([["", root]]);
  for (const fp of filePaths) {
    const segs = fp.split("/");
    let parentPath = "";
    let parent = root;
    for (let i = 0; i < segs.length - 1; i++) {
      const name = segs[i];
      const path = parentPath ? `${parentPath}/${name}` : name;
      let node = byPath.get(path);
      if (!node) {
        node = { path, name, count: 0, children: [] };
        byPath.set(path, node);
        parent.children.push(node);
      }
      node.count += 1;
      parentPath = path;
      parent = node;
    }
  }
  const sortRec = (n: DirNode) => { n.children.sort((a, b) => b.count - a.count); n.children.forEach(sortRec); };
  sortRec(root);
  return root;
}

const POV_OPTIONS: { value: NetworkPov; label: string; help: string }[] = [
  { value: "usage", label: "Usage (how often used)", help: "Inbound dependency count" },
  { value: "altitude", label: "Altitude (call hierarchy)", help: "High up the call tree = bigger (reach down − up)" },
  { value: "functions", label: "Function count / fan-out", help: "Files: # functions · Functions: # calls out" },
  { value: "connections", label: "Connections (degree)", help: "Edges touching the node" },
];

export default function RepoNetworkModal() {
  const dispatch = useAppDispatch();
  const actions = useActions();

  const open = useAppSelector(selectNetworkOpen);
  const repoId = useAppSelector(selectNetworkRepoId);
  const status = useAppSelector(selectNetworkStatus);
  const error = useAppSelector(selectNetworkError);
  const nodes = useAppSelector(selectNetworkNodes);
  const edges = useAppSelector(selectNetworkEdges);
  const selectedId = useAppSelector(selectNetworkSelected);
  const multiSelected = useAppSelector(selectNetworkMultiSelected);
  const nodeColors = useAppSelector(selectNetworkNodeColors);
  const focusFile = useAppSelector(selectNetworkFocusFile);
  const pov = useAppSelector(selectNetworkPov);
  const mode = useAppSelector(selectNetworkMode);
  const user = useAppSelector(selectUser);

  // Methods the user has designated as features this session (inline feedback).
  // Anchored as owner_kind 'feature' — the curated (gold) tier, distinct from the
  // optional IBAction auto-detector (coral). FEATURE view spiders each one's flow.
  const [markedMethods, setMarkedMethods] = React.useState<Set<number>>(new Set());
  const markAsFeature = React.useCallback((methodId: number) => {
    if (!user) return;
    apiAnchorMethod(user.default_organization_id, user.default_workspace_id, repoId, methodId, "feature")
      .then(() => setMarkedMethods((prev) => new Set(prev).add(methodId)))
      .catch(() => { /* leave unmarked so the user can retry */ });
  }, [user, repoId]);

  // Suggested entry points (IBAction candidates) — read-only proposals the user
  // confirms one-by-one. Lazily loaded when the panel is first opened.
  const [suggestOpen, setSuggestOpen] = React.useState(false);
  const [candLoaded, setCandLoaded] = React.useState(false);
  const [candidates, setCandidates] = React.useState<{ method_id: number; name: string; class_name?: string | null; file_path?: string | null }[]>([]);
  const toggleSuggestions = React.useCallback(() => {
    setSuggestOpen((v) => !v);
    if (!candLoaded && user) {
      apiLaunchCandidates(user.default_organization_id, user.default_workspace_id, repoId)
        .then((r: any) => { setCandidates(r?.data?.candidates ?? []); setCandLoaded(true); })
        .catch(() => { /* ignore — button stays available to retry */ });
    }
  }, [candLoaded, user, repoId]);
  const pendingCandidates = candidates.filter((c) => !markedMethods.has(c.method_id));
  const hiddenDirs = useAppSelector(selectNetworkHiddenDirs);
  const indexStatus = useAppSelector(selectIndexStatus);

  // Layout + "show unconnected files" are view-only (not persisted).
  const [layoutKind, setLayoutKind] = React.useState<LayoutKind>("force");
  const [showUnconnected, setShowUnconnected] = React.useState(false);

  // Box-select ("drag a window to select") mode + the live rubber-band rectangle
  // (in container-pixel coords). When the mode is on, an overlay intercepts drags
  // so they draw a selection box instead of panning the camera.
  const [boxSelectMode, setBoxSelectMode] = React.useState(false);
  const [selRect, setSelRect] = React.useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const selRectRef = React.useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  const dragStartRef = React.useRef<{ x: number; y: number; additive: boolean } | null>(null);

  // Directory filters + (by default) drop unconnected file nodes → the rendered set.
  const visibleNodes = React.useMemo(() => {
    const dirNodes = hiddenDirs.length ? nodes.filter((n) => !isHidden(n.filePath, hiddenDirs)) : nodes;
    // Function-focus view: show ONLY functions (the file graph is hidden).
    if (focusFile) return dirNodes.filter((n) => n.kind === "function");
    if (showUnconnected) return dirNodes;
    const dirIds = new Set(dirNodes.map((n) => n.id));
    const connected = new Set<string>();
    for (const e of edges) {
      if (dirIds.has(e.source) && dirIds.has(e.target)) { connected.add(e.source); connected.add(e.target); }
    }
    // Keep all functions; keep files only if they have a dependency edge.
    return dirNodes.filter((n) => n.kind !== "file" || connected.has(n.id));
  }, [nodes, edges, hiddenDirs, showUnconnected, focusFile]);
  const visibleIds = React.useMemo(() => new Set(visibleNodes.map((n) => n.id)), [visibleNodes]);
  const visibleEdges = React.useMemo(
    () => edges.filter((e) => visibleIds.has(e.source) && visibleIds.has(e.target)),
    [edges, visibleIds]
  );
  // Degree per node id (for the "connections" sizing POV).
  const degreeMap = React.useMemo(() => {
    const m = new Map<string, number>();
    for (const e of visibleEdges) {
      m.set(e.source, (m.get(e.source) ?? 0) + 1);
      m.set(e.target, (m.get(e.target) ?? 0) + 1);
    }
    return m;
  }, [visibleEdges]);
  // Nested directory tree (all depths) for the filter menu.
  const dirTree = React.useMemo(
    () => buildDirTree(nodes.filter((n) => n.kind === "file" && n.filePath).map((n) => n.filePath as string)),
    [nodes]
  );
  // Which tree directories are expanded in the menu.
  const [expandedDirs, setExpandedDirs] = React.useState<Set<string>>(new Set());

  // Files whose functions are currently shown (i.e. expanded) — for the close panel.
  const expandedFiles = React.useMemo(() => {
    const fnPaths = new Set(nodes.filter((n) => n.kind === "function" && n.filePath).map((n) => n.filePath));
    return nodes.filter((n) => n.kind === "file" && n.filePath && fnPaths.has(n.filePath));
  }, [nodes]);

  // File-name search (bottom-right).
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const searchResults = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [] as NetworkNode[];
    // Search what's actually on screen: in function-focus (a file expanded into
    // its functions) that's the functions; otherwise the file graph. This keeps
    // results in sync with the view and guarantees the pan-to-node target exists.
    return visibleNodes
      .filter((n) => n.label.toLowerCase().includes(q) || (n.filePath ?? "").toLowerCase().includes(q))
      .slice(0, 40);
  }, [visibleNodes, searchQuery]);

  // Right-click context menu ("Show code!").
  const [ctxMenu, setCtxMenu] = React.useState<{ x: number; y: number; node: NetworkNode } | null>(null);

  // "Show code" modal selectors.
  const codeOpen = useAppSelector(selectNetworkCodeOpen);
  const codeTitle = useAppSelector(selectNetworkCodeTitle);
  const codeSubtitle = useAppSelector(selectNetworkCodeSubtitle);
  const codeBody = useAppSelector(selectNetworkCodeBody);
  const codeStatus = useAppSelector(selectNetworkCodeStatus);

  // Bulb size from the active POV.
  const sizeForNode = React.useCallback((node: NetworkNode): number => {
    let v: number;
    if (pov === "connections") {
      v = degreeMap.get(node.id) ?? 0;
    } else if (pov === "functions") {
      v = node.kind === "file" ? (node.methodCount ?? 0) : (node.fanout ?? 0);
    } else if (pov === "altitude") {
      // High in the call hierarchy = reaches far downstream, called from little upstream.
      v = Math.max(0, (node.transitiveOut ?? 0) - (node.transitiveIn ?? 0));
    } else {
      v = node.usage ?? 0; // usage (default)
    }
    return Math.min(22, 3 + Math.sqrt(v) * 1.7);
  }, [pov, degreeMap]);

  // Settings menu anchor.
  const [settingsAnchor, setSettingsAnchor] = React.useState<HTMLElement | null>(null);

  const persistSettings = React.useCallback((nextPov: NetworkPov, nextHidden: string[], nextColors: Record<string, string>) => {
    actions.saveGraphSettings({ repoId, pov: nextPov, hiddenDirs: nextHidden, nodeColors: nextColors });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoId]);

  // Paint the current selection a colour (null = erase), persist, and drop the
  // selection so the colour is immediately visible. We compute the next map here
  // (rather than in a reducer) so the same map is what we persist.
  const colorizeSelection = (color: string | null) => {
    const next = { ...nodeColors };
    for (const id of multiSelected) {
      if (color === null) delete next[id];
      else next[id] = color;
    }
    dispatch(setNetworkNodeColors(next));
    dispatch(clearNetworkSelected());
    persistSettings(pov, hiddenDirs, next);
  };
  const resetColors = () => {
    dispatch(clearNetworkNodeColors());
    persistSettings(pov, hiddenDirs, {});
  };

  // --- Directory filter tree (nested, any depth; persisted) ---
  const toggleDirExpand = (path: string) => {
    setExpandedDirs((s) => { const n = new Set(s); if (n.has(path)) n.delete(path); else n.add(path); return n; });
  };
  // Is a STRICT ancestor of this dir hidden (so this dir is hidden by inheritance)?
  const ancestorHidden = (path: string) => hiddenDirs.some((d) => d !== path && path.startsWith(d + "/"));
  const toggleDirHidden = (path: string) => {
    const next = hiddenDirs.includes(path)
      ? hiddenDirs.filter((d) => d !== path)
      // Hide this dir and drop any descendant entries it now subsumes.
      : hiddenDirs.filter((d) => d !== path && !d.startsWith(path + "/")).concat(path);
    dispatch(setNetworkHiddenDirs(next));
    persistSettings(pov, next, nodeColors);
  };
  // Flatten the visible (expanded) part of the tree into indented rows.
  const renderDirRows = (): React.ReactNode[] => {
    const rows: React.ReactNode[] = [];
    const walk = (node: DirNode, depth: number) => {
      for (const child of node.children) {
        const hidden = isHidden(child.path, hiddenDirs);
        const ancHidden = ancestorHidden(child.path);
        const hasChildren = child.children.length > 0;
        const isExpanded = expandedDirs.has(child.path);
        rows.push(
          <Box key={child.path} sx={{ display: "flex", alignItems: "center", pl: depth * 1.5 }}>
            {hasChildren ? (
              <IconButton size="small" sx={{ p: 0.25, color: "#7f8cb5" }} onClick={() => toggleDirExpand(child.path)}>
                {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 24 }} />
            )}
            <Checkbox
              size="small"
              checked={!hidden}
              disabled={ancHidden}
              sx={{ py: 0.25, color: "#7f8cb5", "&.Mui-checked": { color: "#7ee0a0" } }}
              onChange={() => toggleDirHidden(child.path)}
            />
            <Typography
              variant="body2"
              title={child.path}
              sx={{ fontFamily: "monospace", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {child.name} <span style={{ color: "#6b7693" }}>· {child.count}</span>
            </Typography>
          </Box>
        );
        if (isExpanded && hasChildren) walk(child, depth + 1);
      }
    };
    walk(dirTree, 0);
    return rows;
  };

  // Diagnostics for the on-canvas debug line: what we last pushed to the graph
  // and the live canvas size. Lets us distinguish "data never reached the graph"
  // from "canvas is zero-size / dead" from "rendered but not visible".
  const [renderInfo, setRenderInfo] = React.useState<{ points: number; links: number; w: number; h: number }>({ points: 0, links: 0, w: 0, h: 0 });
  // If Sigma construction throws (e.g. WebGL unavailable), surface it on-canvas.
  const [graphError, setGraphError] = React.useState<string | null>(null);

  // Rolling feed of recently-touched files, sampled from each status poll.
  const [feed, setFeed] = React.useState<string[]>([]);
  const progress = indexStatus?.stats;
  const currentFile = progress?.current_file ?? "";
  React.useEffect(() => {
    if (!currentFile) return;
    setFeed((prev) => (prev[0] === currentFile ? prev : [currentFile, ...prev].slice(0, 12)));
  }, [currentFile]);
  // Clear the feed when a new run starts (queued with no progress yet).
  React.useEffect(() => {
    if (indexStatus?.status === "queued") setFeed([]);
  }, [indexStatus?.status]);

  // Worker liveness from the updated_at heartbeat. Parsing writes every ~300ms so
  // a stale heartbeat there is meaningful; resolving/counting are whole-graph
  // phases that legitimately don't write per-file, so they get a much longer grace
  // window before we'd call the worker unresponsive.
  const phase = progress?.phase;
  const updatedMs = indexStatus?.updated_at ? Date.parse(indexStatus.updated_at) : 0;
  const sinceUpdate = updatedMs > 0 ? Date.now() - updatedMs : Infinity;
  const isParsingPhase = !phase || phase === "parsing";
  const heartbeatFresh = sinceUpdate < (isParsingPhase ? 30000 : 120000);
  const workerActive = indexStatus?.status === "running" && heartbeatFresh;
  const workerStalled = indexStatus?.status === "running" && !heartbeatFresh;
  const showFeed = indexStatus?.status === "queued" || indexStatus?.status === "running";
  const filesDone = progress?.files_done ?? 0;
  const filesTotal = progress?.files_total ?? 0;

  const containerRef = React.useRef<HTMLDivElement | null>(null);
  // Callback ref → state, so an effect re-runs the moment the container mounts
  // (a plain ref wouldn't trigger a re-render, and the dialog mounts its content
  // a tick after `open` flips — that race is what left the graph uncreated).
  const [containerEl, setContainerElState] = React.useState<HTMLDivElement | null>(null);
  const setContainerRef = React.useCallback((el: HTMLDivElement | null) => {
    containerRef.current = el;
    setContainerElState(el);
  }, []);
  const graphRef = React.useRef<Graph | null>(null);
  const sigmaRef = React.useRef<any>(null);

  const selectedNode: NetworkNode | undefined =
    selectedId ? nodes.find((n) => n.id === selectedId) : undefined;

  // Visible nodes by id, for the (stable) click handler.
  const nodesRef = React.useRef<NetworkNode[]>(visibleNodes);
  nodesRef.current = visibleNodes;

  // selectedId / multi-selection in refs so sigma's node reducer reads the latest.
  const selectedRef = React.useRef<string | null>(selectedId);
  selectedRef.current = selectedId;
  const multiRef = React.useRef<Set<string>>(new Set(multiSelected));
  multiRef.current = new Set(multiSelected);
  const colorsRef = React.useRef<Record<string, string>>(nodeColors);
  colorsRef.current = nodeColors;

  // node id + whether a modifier (shift/ctrl/meta) was held → additive toggle.
  const handleNodeClick = React.useCallback((id: string | undefined, additive?: boolean) => {
    // Click on empty space: clear (but a modifier-click on empty space keeps the set).
    if (!id) { if (!additive) dispatch(setNetworkSelected(null)); return; }
    const node = nodesRef.current.find((n) => n.id === id);
    if (!node) { dispatch(setNetworkSelected(null)); return; }
    // Clicking only SELECTS — expansion is button-driven from the side panel.
    // Shift/ctrl/meta-click toggles the node in the multi-selection instead.
    dispatch(additive ? toggleNetworkSelected(node.id) : setNetworkSelected(node.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, repoId]);

  const clickRef = React.useRef(handleNodeClick);
  clickRef.current = handleNodeClick;

  // Right-click → open the context menu at the cursor for that node.
  const rightClickRef = React.useRef<(id: string, x: number, y: number) => void>(() => undefined);
  rightClickRef.current = (id, x, y) => {
    const node = nodesRef.current.find((n) => n.id === id);
    if (node) setCtxMenu({ x, y, node });
  };

  const layoutRef = React.useRef<LayoutKind>(layoutKind);
  layoutRef.current = layoutKind;

  const runLayout = React.useCallback(() => {
    const graph = graphRef.current;
    if (!graph) return;
    try {
      runGraphLayout(graph, layoutRef.current);
      sigmaRef.current?.refresh();
      sigmaRef.current?.getCamera().animatedReset();
    } catch { /* noop */ }
  }, []);

  const featureViewRef = React.useRef<{ fit: () => void } | null>(null);
  const fitView = React.useCallback(() => {
    if (mode === "feature") { featureViewRef.current?.fit(); return; }
    try { sigmaRef.current?.getCamera().animatedReset(); } catch { /* noop */ }
  }, [mode]);

  // Select a node from search and pan/zoom the camera onto it (if it's rendered).
  const focusNode = React.useCallback((node: NetworkNode) => {
    dispatch(setNetworkSelected(node.id));
    const sigma = sigmaRef.current;
    const graph = graphRef.current;
    if (sigma && graph && graph.hasNode(node.id)) {
      try {
        const dd = sigma.getNodeDisplayData(node.id);
        if (dd) sigma.getCamera().animate({ x: dd.x, y: dd.y, ratio: 0.3 }, { duration: 500 });
      } catch { /* noop */ }
    }
  }, [dispatch]);

  // --- Box-select ("drag a window to select") ---
  // The overlay sits above the sigma canvas only while boxSelectMode is on, so its
  // drag never reaches sigma's camera. We map the rectangle's corners back into
  // graph space via viewportToGraph and select every node inside.
  const boxMouseDown = (e: React.MouseEvent) => {
    const host = containerRef.current;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    dragStartRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      additive: e.shiftKey || e.ctrlKey || e.metaKey,
    };
    const r0 = { x: e.clientX - rect.left, y: e.clientY - rect.top, w: 0, h: 0 };
    selRectRef.current = r0;
    setSelRect(r0);
  };
  const boxMouseMove = (e: React.MouseEvent) => {
    const start = dragStartRef.current;
    const host = containerRef.current;
    if (!start || !host) return;
    const rect = host.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const r = { x: Math.min(start.x, cx), y: Math.min(start.y, cy), w: Math.abs(cx - start.x), h: Math.abs(cy - start.y) };
    selRectRef.current = r;
    setSelRect(r);
  };
  const boxMouseUp = () => {
    const start = dragStartRef.current;
    const rectPx = selRectRef.current;
    dragStartRef.current = null;
    selRectRef.current = null;
    setSelRect(null);
    const sigma = sigmaRef.current;
    const graph = graphRef.current;
    if (!start || !sigma || !graph) return;
    // A click with no real drag → clear the selection.
    if (!rectPx || (rectPx.w < 3 && rectPx.h < 3)) {
      if (!start.additive) dispatch(clearNetworkSelected());
      return;
    }
    // Rectangle corners → graph space (accounts for pan/zoom).
    const a = sigma.viewportToGraph({ x: rectPx.x, y: rectPx.y });
    const b = sigma.viewportToGraph({ x: rectPx.x + rectPx.w, y: rectPx.y + rectPx.h });
    const minX = Math.min(a.x, b.x), maxX = Math.max(a.x, b.x);
    const minY = Math.min(a.y, b.y), maxY = Math.max(a.y, b.y);
    const hits: string[] = [];
    graph.forEachNode((id: string, attr: any) => {
      if (attr.x >= minX && attr.x <= maxX && attr.y >= minY && attr.y <= maxY) hits.push(id);
    });
    const next = start.additive ? Array.from(new Set([...multiSelected, ...hits])) : hits;
    dispatch(setNetworkMultiSelected(next));
  };

  // "Show code!" — function block or whole file, in a modal.
  const showCode = (node: NetworkNode) => {
    setCtxMenu(null);
    if (node.kind === "function" && node.methodId !== undefined) {
      actions.showNetworkCode({ repoId, methodId: node.methodId, title: node.label, subtitle: node.filePath ?? "" });
    } else if (node.kind === "file" && node.filePath) {
      actions.showNetworkCode({ repoId, filePath: node.filePath, title: node.label, subtitle: node.filePath });
    }
  };

  // Kill sigma on close / unmount.
  React.useEffect(() => {
    if (open) return;
    if (sigmaRef.current) { try { sigmaRef.current.kill(); } catch { /* noop */ } sigmaRef.current = null; }
    graphRef.current = null;
  }, [open]);
  React.useEffect(() => () => {
    if (sigmaRef.current) { try { sigmaRef.current.kill(); } catch { /* noop */ } sigmaRef.current = null; }
    graphRef.current = null;
  }, []);

  // Create the sigma instance + graphology graph once the container exists, then
  // diff data into the graph. Layout runs ONCE → static positions (no drift);
  // expansions place new nodes near a neighbour without reshuffling the rest.
  React.useEffect(() => {
    if (!open) return;
    const container = containerEl;
    if (!container) return;

    if (!sigmaRef.current) {
      try {
        const graph = new Graph({ type: "directed", multi: false });
        const sigma = new Sigma(graph, container, {
          renderLabels: true,
          labelColor: { color: "#dbe6ff" },
          labelFont: "ui-monospace, monospace",
          labelSize: 12,
          // Sigma keeps labels a constant on-screen size and only draws them for
          // nodes large enough on screen — exactly the zoom-in-to-read behaviour.
          labelRenderedSizeThreshold: 8,
          labelDensity: 0.5,
          labelGridCellSize: 70,
          defaultNodeColor: COLOR_FILE,
          defaultEdgeColor: COLOR_EDGE,
          // Readable labels: white box + black text (see drawNodeLabel/drawNodeHover).
          defaultDrawNodeLabel: drawNodeLabel,
          defaultDrawNodeHover: drawNodeHover,
          minCameraRatio: 0.02,
          maxCameraRatio: 20,
          nodeReducer: (node: string, data: any) => {
            // Any node in the multi-selection is highlighted amber. The PRIMARY one
            // additionally gets forceLabel + sel so its title renders black-on-white.
            if (multiRef.current.has(node) || node === selectedRef.current) {
              const isPrimary = node === selectedRef.current;
              return { ...data, color: COLOR_SELECTED, zIndex: 1, forceLabel: isPrimary, sel: isPrimary };
            }
            // Otherwise, a user-assigned group colour overrides the default file/fn colour.
            const assigned = colorsRef.current[node];
            if (assigned) return { ...data, color: assigned };
            return data;
          },
        });
        sigma.on("clickNode", (e: any) => {
          const orig = e?.event?.original;
          clickRef.current(e.node, !!(orig && (orig.shiftKey || orig.ctrlKey || orig.metaKey)));
        });
        sigma.on("clickStage", (e: any) => {
          const orig = e?.event?.original;
          clickRef.current(undefined, !!(orig && (orig.shiftKey || orig.ctrlKey || orig.metaKey)));
        });
        sigma.on("rightClickNode", (e: any) => {
          const orig = e?.event?.original;
          if (orig && typeof orig.preventDefault === "function") orig.preventDefault();
          rightClickRef.current(e.node, orig?.clientX ?? 0, orig?.clientY ?? 0);
        });
        graphRef.current = graph;
        sigmaRef.current = sigma;
        setGraphError(null);
        // eslint-disable-next-line no-console
        console.log("[RepoNetwork] sigma created", container.clientWidth, "x", container.clientHeight);
      } catch (err) {
        setGraphError(String(err));
        // eslint-disable-next-line no-console
        console.error("[RepoNetwork] Sigma() failed:", err);
        return;
      }
    }

    const graph = graphRef.current;
    const sigma = sigmaRef.current;
    if (!graph || !sigma) return;

    const desiredNodeIds = new Set(visibleNodes.map((n) => n.id));
    const wasEmpty = graph.order === 0;

    let added = 0;
    let removed = 0;

    // Remove stale nodes (collect first; dropping also removes their edges).
    const stale: string[] = [];
    graph.forEachNode((n) => { if (!desiredNodeIds.has(n)) stale.push(n); });
    stale.forEach((n) => { graph.dropNode(n); removed++; });

    // Place a new node near an already-positioned neighbour, else near origin.
    const neighbourPos = (id: string): { x: number; y: number } | null => {
      for (const e of visibleEdges) {
        const other = e.source === id ? e.target : e.target === id ? e.source : null;
        if (other && graph.hasNode(other)) {
          return {
            x: graph.getNodeAttribute(other, "x") + (Math.random() - 0.5) * 30,
            y: graph.getNodeAttribute(other, "y") + (Math.random() - 0.5) * 30,
          };
        }
      }
      return null;
    };

    for (const node of visibleNodes) {
      const sz = sizeForNode(node);
      const color = node.kind === "file" ? COLOR_FILE : COLOR_FUNCTION;
      if (graph.hasNode(node.id)) {
        graph.setNodeAttribute(node.id, "size", sz);
        graph.setNodeAttribute(node.id, "label", node.label);
        graph.setNodeAttribute(node.id, "color", color);
      } else {
        const p = neighbourPos(node.id) ?? { x: (Math.random() - 0.5) * 200, y: (Math.random() - 0.5) * 200 };
        graph.addNode(node.id, { x: p.x, y: p.y, size: sz, label: node.label, color, kind: node.kind });
        added++;
      }
    }

    // Add desired edges (skip existing / missing endpoints).
    for (const e of visibleEdges) {
      if (!desiredNodeIds.has(e.source) || !desiredNodeIds.has(e.target)) continue;
      const key = `${e.source}__${e.target}`;
      if (!graph.hasEdge(key)) {
        try { graph.addEdgeWithKey(key, e.source, e.target, { size: 0.6 }); } catch { /* dup */ }
      }
    }

    // Full layout on first population or a wholesale replacement (repo switch);
    // expansions (added only) / collapses (removed only) keep positions.
    if ((wasEmpty || (added > 0 && removed > 0)) && graph.order > 0) {
      runGraphLayout(graph, layoutRef.current);
      sigma.getCamera().animatedReset();
    }
    sigma.refresh();

    setRenderInfo({
      points: graph.order,
      links: graph.size,
      w: container.clientWidth,
      h: container.clientHeight,
    });
  }, [visibleNodes, visibleEdges, selectedId, open, containerEl, sizeForNode]);

  // Re-run layout when the layout kind changes.
  React.useEffect(() => {
    if (!open || !graphRef.current) return;
    runLayout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutKind]);

  // Repaint selection highlights / group colours immediately when they change
  // (the heavy data effect only re-runs on node/edge churn).
  React.useEffect(() => {
    try { sigmaRef.current?.refresh(); } catch { /* noop */ }
  }, [multiSelected, selectedId, nodeColors]);

  // Record the graph view as the current navigation location (for bookmarks /
  // "current location"); reset to library when the modal closes.
  React.useEffect(() => {
    if (open) dispatch(setNavLocation({ kind: "graph", networkMode: mode, networkRepoId: repoId }));
    else dispatch(resetNavLocation());
  }, [open, mode, repoId, dispatch]);

  const handleClose = () => dispatch(closeNetwork());

  const handleRefresh = () => {
    actions.loadFileGraph({ repoId });
    actions.indexRepoBackground({ repoId, jobType: "INCREMENTAL" });
    actions.pollIndexStatus({ repoId, untilDone: true });
  };

  // Purge: wipe the repo's extracted index and rebuild from scratch. Use after
  // parser/schema changes when an incremental rebuild would keep stale rows.
  // Overlay anchors/bundles survive (they re-resolve by coordinate).
  const handlePurge = () => {
    if (!window.confirm(
      "Purge and re-index this repo from scratch?\n\nThis wipes all extracted files, functions, and call data, then re-trawls every file. It can take a while on a large repo. Anchors and bundles are preserved.",
    )) return;
    setSettingsAnchor(null);
    actions.indexRepoBackground({ repoId, jobType: "PURGE" });
    actions.pollIndexStatus({ repoId, untilDone: true });
  };


  const indexing = indexStatus?.status === "queued" || indexStatus?.status === "running";

  return (
    <Dialog fullScreen open={open} onClose={handleClose}>
      <AppBar position="relative" sx={{ bgcolor: "#0b1220" }}>
        <Toolbar variant="dense">
          <AccountTreeIcon sx={{ mr: 1 }} />
          <Typography variant="subtitle1" sx={{ mr: 2 }}>
            Dependency network
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_e, next: NetworkMode | null) => { if (next) dispatch(setNetworkMode(next)); }}
            sx={{
              mr: 2,
              "& .MuiToggleButton-root": { color: "#8893b5", borderColor: "#33406a", textTransform: "none", py: 0.25, px: 1.25 },
              "& .Mui-selected": { color: "#e6ebff !important", bgcolor: "#1c3a73 !important" },
            }}
          >
            <ToggleButton value="structural">Structural</ToggleButton>
            <ToggleButton value="feature">Feature</ToggleButton>
          </ToggleButtonGroup>
          {indexing && (
            <Chip
              size="small"
              color="warning"
              icon={<CircularProgress size={12} color="inherit" />}
              label={`Indexing (${indexStatus?.status})…`}
              sx={{ mr: 1 }}
            />
          )}
          {indexStatus?.status === "completed" && indexStatus?.stats && (
            <Chip
              size="small"
              variant="outlined"
              sx={{ mr: 1, color: "#cfd8ff", borderColor: "#33406a" }}
              label={`${indexStatus.stats.files} files · ${indexStatus.stats.methods} fns · ${indexStatus.stats.dependencies} deps`}
            />
          )}
          <Typography variant="body2" sx={{ color: "#8893b5", mr: 2 }}>
            {visibleNodes.length} nodes · {visibleEdges.length} edges
            {hiddenDirs.length > 0 ? ` · ${nodes.length - visibleNodes.length} hidden` : ""}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {mode === "structural" && (
            <Tooltip title="Suggested entry points (IBAction) to review and confirm as features">
              <Button
                size="small"
                onClick={toggleSuggestions}
                sx={{ color: "#9fe6b8", textTransform: "none", mr: 1 }}
              >
                Suggestions{candLoaded ? ` (${pendingCandidates.length})` : ""}
              </Button>
            </Tooltip>
          )}
          <BookmarkMenu workspaces={undefined} workspaceId={undefined} />
          <Tooltip title="Sizing & directory filters">
            <IconButton color="inherit" onClick={(e) => setSettingsAnchor(e.currentTarget)}><TuneIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Fit graph to view">
            <IconButton color="inherit" onClick={fitView}><CenterFocusStrongIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Re-index & refresh">
            <IconButton color="inherit" onClick={handleRefresh}><RefreshIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton color="inherit" onClick={handleClose}><CloseIcon /></IconButton>
          </Tooltip>
        </Toolbar>

        {/* Selection tools: click-select vs drag-a-window box-select. */}
        <Toolbar variant="dense" sx={{ minHeight: 40, borderTop: "1px solid #1b2540", bgcolor: "#0e1626" }}>
          <Typography variant="caption" sx={{ color: "#8893b5", mr: 1.5 }}>Select</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={boxSelectMode ? "box" : "click"}
            onChange={(_e, next: string | null) => { if (next) setBoxSelectMode(next === "box"); }}
            sx={{
              mr: 2,
              "& .MuiToggleButton-root": { color: "#8893b5", borderColor: "#33406a", textTransform: "none", py: 0.25, px: 1.25, gap: 0.5 },
              "& .Mui-selected": { color: "#e6ebff !important", bgcolor: "#1c3a73 !important" },
            }}
          >
            <ToggleButton value="click"><NearMeIcon fontSize="small" /> Click</ToggleButton>
            <ToggleButton value="box"><HighlightAltIcon fontSize="small" /> Box select</ToggleButton>
          </ToggleButtonGroup>
          {boxSelectMode && (
            <Typography variant="caption" sx={{ color: "#7f8cb5", mr: 2 }}>
              Drag a window over nodes · hold Shift to add
            </Typography>
          )}

          {/* Colour the selected group. Swatches are disabled until something is
              selected; clicking one paints the selection and clears it so the
              colour is immediately visible. */}
          <Divider orientation="vertical" flexItem sx={{ mx: 1.5, borderColor: "#27304d" }} />
          <Tooltip title={multiSelected.length ? "Colour the selected group" : "Select nodes first"}>
            <Typography variant="caption" sx={{ color: multiSelected.length ? "#cfd8ff" : "#5a6584", mr: 1 }}>Colour</Typography>
          </Tooltip>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, opacity: multiSelected.length ? 1 : 0.4, pointerEvents: multiSelected.length ? "auto" : "none" }}>
            {GROUP_COLORS.map((c) => (
              <Box
                key={c}
                onClick={() => colorizeSelection(c)}
                title={c}
                sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: c, cursor: "pointer", border: "1px solid rgba(255,255,255,0.25)", "&:hover": { transform: "scale(1.18)" }, transition: "transform 80ms" }}
              />
            ))}
            <Tooltip title="Remove colour from selection">
              <Box
                onClick={() => colorizeSelection(null)}
                sx={{ width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", border: "1px solid #5a6584", color: "#9fb0d0", "&:hover": { borderColor: "#8aa0c8" } }}
              >
                <CloseIcon sx={{ fontSize: 12 }} />
              </Box>
            </Tooltip>
          </Box>

          <Box sx={{ flex: 1 }} />
          {Object.keys(nodeColors).length > 0 && (
            <Button size="small" onClick={resetColors} sx={{ color: "#9fb4ff", textTransform: "none", mr: 1 }}>
              Reset colours
            </Button>
          )}
          <Typography variant="body2" sx={{ color: multiSelected.length ? "#f5c46a" : "#8893b5", mr: 1 }}>
            {multiSelected.length} selected
          </Typography>
          <Tooltip title="Clear selection">
            <span>
              <Button
                size="small"
                variant="contained"
                color="inherit"
                startIcon={<DeselectIcon />}
                disabled={multiSelected.length === 0}
                onClick={() => dispatch(clearNetworkSelected())}
                sx={{ textTransform: "none", bgcolor: "#22304f", color: "#dbe6ff", "&:hover": { bgcolor: "#2c3d63" } }}
              >
                Clear
              </Button>
            </span>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Sizing POV + directory filters (persisted per repo) */}
      <Popover
        open={Boolean(settingsAnchor)}
        anchorEl={settingsAnchor}
        onClose={() => setSettingsAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { bgcolor: "#141a2b", color: "#e6ebff", p: 2, width: 330 } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="subtitle2">Layout</Typography>
          <Button size="small" sx={{ color: "#9fb4ff" }} onClick={() => runLayout()}>
            Re-run
          </Button>
        </Box>
        <RadioGroup
          row
          value={layoutKind}
          onChange={(e) => setLayoutKind(e.target.value as LayoutKind)}
          sx={{ mb: 1 }}
        >
          <FormControlLabel value="force" control={<Radio size="small" sx={{ py: 0.25, color: "#7f8cb5", "&.Mui-checked": { color: "#7ea6ff" } }} />} label={<Typography variant="body2">Force</Typography>} />
          <FormControlLabel value="hierarchy" control={<Radio size="small" sx={{ py: 0.25, color: "#7f8cb5", "&.Mui-checked": { color: "#7ea6ff" } }} />} label={<Typography variant="body2">Hierarchy</Typography>} />
        </RadioGroup>
        <FormControlLabel
          sx={{ m: 0, mb: 1 }}
          control={
            <Checkbox
              size="small"
              checked={showUnconnected}
              sx={{ py: 0.25, color: "#7f8cb5", "&.Mui-checked": { color: "#7ee0a0" } }}
              onChange={(e) => setShowUnconnected(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Show unconnected files</Typography>}
        />

        <Divider sx={{ my: 1.5, borderColor: "#27304d" }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Bulb size by</Typography>
        <RadioGroup
          value={pov}
          onChange={(e) => {
            const next = e.target.value as NetworkPov;
            dispatch(setNetworkPov(next));
            persistSettings(next, hiddenDirs, nodeColors);
          }}
        >
          {POV_OPTIONS.map((o) => (
            <FormControlLabel
              key={o.value}
              value={o.value}
              sx={{ alignItems: "flex-start", mb: 0.5 }}
              control={<Radio size="small" sx={{ py: 0.25, color: "#7f8cb5", "&.Mui-checked": { color: "#7ea6ff" } }} />}
              label={
                <Box>
                  <Typography variant="body2">{o.label}</Typography>
                  <Typography variant="caption" sx={{ color: "#8893b5" }}>{o.help}</Typography>
                </Box>
              }
            />
          ))}
        </RadioGroup>

        <Divider sx={{ my: 1.5, borderColor: "#27304d" }} />

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
          <Typography variant="subtitle2">Directories</Typography>
          {hiddenDirs.length > 0 && (
            <Button size="small" sx={{ color: "#9fb4ff" }} onClick={() => { dispatch(setNetworkHiddenDirs([])); persistSettings(pov, [], nodeColors); }}>
              Show all
            </Button>
          )}
        </Box>
        <Typography variant="caption" sx={{ color: "#8893b5", display: "block", mb: 0.5 }}>
          Expand a folder to drill in; uncheck to hide it and everything under it
          (e.g. vendor dirs at any depth). Saved per repo.
        </Typography>
        <Box sx={{ maxHeight: 300, overflow: "auto" }}>
          {dirTree.children.length === 0 ? (
            <Typography variant="caption" sx={{ color: "#8893b5" }}>No directories.</Typography>
          ) : (
            renderDirRows()
          )}
        </Box>

        <Divider sx={{ my: 1.5, borderColor: "#27304d" }} />

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Maintenance</Typography>
        <Typography variant="caption" sx={{ color: "#8893b5", display: "block", mb: 1 }}>
          Wipe the extracted index and re-trawl every file from scratch. Use after
          parser or schema changes; an incremental refresh keeps stale rows. Anchors
          and bundles are preserved.
        </Typography>
        <Button
          size="small"
          variant="outlined"
          color="error"
          disabled={indexing}
          startIcon={<RefreshIcon />}
          onClick={handlePurge}
          sx={{ textTransform: "none" }}
        >
          {indexing ? "Indexing…" : "Purge & re-index"}
        </Button>
      </Popover>

      <Box sx={{ position: "relative", flex: 1, height: "100%", bgcolor: "#0b1220" }} onContextMenu={(e) => e.preventDefault()}>
        <div ref={setContainerRef} style={{ position: "absolute", inset: 0 }} />

        {/* Box-select overlay: only mounted in box mode, so normal click/pan is
            untouched otherwise. It intercepts the drag (so sigma never pans) and
            draws the rubber-band rectangle. */}
        {boxSelectMode && (
          <div
            style={{ position: "absolute", inset: 0, zIndex: 4, cursor: "crosshair" }}
            onMouseDown={boxMouseDown}
            onMouseMove={boxMouseMove}
            onMouseUp={boxMouseUp}
            onMouseLeave={() => { if (dragStartRef.current) boxMouseUp(); }}
          >
            {selRect && (
              <div
                style={{
                  position: "absolute",
                  left: selRect.x, top: selRect.y, width: selRect.w, height: selRect.h,
                  border: "1px solid #f59e0a",
                  background: "rgba(245,158,10,0.12)",
                  pointerEvents: "none",
                }}
              />
            )}
          </div>
        )}

        {/* FEATURE mode: a self-contained overlay that covers (but never touches)
            the structural canvas underneath. Mounts only while active. */}
        {mode === "feature" && <RepoFeatureView ref={featureViewRef} repoId={repoId} />}

        {/* Suggested entry points (IBAction candidates) — confirm into features */}
        {mode === "structural" && suggestOpen && (
          <Paper elevation={8} sx={{ position: "absolute", top: 12, right: 12, width: 380, maxHeight: 420, display: "flex", flexDirection: "column", bgcolor: "#141a2b", color: "#e6ebff", zIndex: 7 }}>
            <Box sx={{ display: "flex", alignItems: "center", p: 1.25, borderBottom: "1px solid #27304d" }}>
              <Typography variant="subtitle2" sx={{ flex: 1 }}>Suggested entry points</Typography>
              <IconButton size="small" sx={{ color: "#8893b5" }} onClick={() => setSuggestOpen(false)}><CloseIcon fontSize="small" /></IconButton>
            </Box>
            <Typography variant="caption" sx={{ color: "#8893b5", px: 1.25, pt: 1 }}>
              IBAction methods (likely UI entry points). Review and confirm the ones
              that are real features — nothing is added to FEATURE mode until you do.
            </Typography>
            <Box sx={{ overflow: "auto", p: 1.25, pt: 1 }}>
              {!candLoaded ? (
                <Typography variant="body2" sx={{ color: "#8893b5" }}>Loading…</Typography>
              ) : pendingCandidates.length === 0 ? (
                <Typography variant="body2" sx={{ color: "#8893b5" }}>
                  {candidates.length === 0 ? "No IBAction candidates found." : "All candidates reviewed."}
                </Typography>
              ) : (
                pendingCandidates.map((c) => (
                  <Box key={c.method_id} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                        {c.class_name ? `${c.class_name}::${c.name}` : c.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#8893b5", wordBreak: "break-all" }}>{c.file_path}</Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => markAsFeature(c.method_id)}
                      sx={{ color: "#e0a82e", borderColor: "#7a5a18", textTransform: "none", flexShrink: 0 }}
                    >
                      Mark
                    </Button>
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        )}

        {/* Top-left: close the function expansion → back to the file graph */}
        {focusFile && (
          <Button
            variant="contained"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => dispatch(exitNetworkFocus())}
            sx={{ position: "absolute", top: 10, left: 10, zIndex: 5, bgcolor: "#1c3a73", "&:hover": { bgcolor: "#264a8c" }, textTransform: "none", maxWidth: 320 }}
            title={focusFile}
          >
            Back to files · {focusFile.split("/").pop()}
          </Button>
        )}

        {/* On-canvas diagnostics: data-reached-graph? canvas alive? (selectable so you can copy it) */}
        <Box
          sx={{
            position: "absolute", top: focusFile ? 48 : 8, left: 8, px: 1, py: 0.5, maxWidth: "90%",
            bgcolor: "rgba(0,0,0,0.6)", color: graphError ? "#ff9aa2" : renderInfo.points > 0 ? "#9fe6b8" : "#ffd479",
            fontFamily: "monospace", fontSize: 11, lineHeight: 1.5, borderRadius: 1, userSelect: "text",
          }}
        >
          state {nodes.length}/{edges.length} · pushed {renderInfo.points}pts/{renderInfo.links}lnk · canvas {renderInfo.w}×{renderInfo.h} · graph {sigmaRef.current ? "ok" : "—"} · container {containerEl ? "ok" : "—"}
          {graphError ? ` · ERROR ${graphError}` : ""}
        </Box>

        {status === "loading" && nodes.length === 0 && (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", color: "#cfd8ff" }}>
            <CircularProgress color="inherit" />
            <Typography sx={{ mt: 2 }}>Loading dependency graph…</Typography>
          </Box>
        )}

        {status === "failed" && (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", color: "#ffb4b4" }}>
            <Typography>Could not load the dependency graph.</Typography>
            <Typography variant="body2" sx={{ mt: 1, color: "#8893b5" }}>{error}</Typography>
            <Button onClick={handleRefresh} variant="contained" sx={{ mt: 2 }}>Retry</Button>
          </Box>
        )}

        {status === "done" && nodes.length === 0 && (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", color: "#cfd8ff" }}>
            <Typography>No dependencies indexed yet.</Typography>
            <Typography variant="body2" sx={{ mt: 1, color: "#8893b5" }}>
              {indexing ? "Indexing is running — this will populate shortly." : "Click refresh to build the dependency map."}
            </Typography>
            <Button onClick={handleRefresh} variant="contained" sx={{ mt: 2 }}>Build index</Button>
          </Box>
        )}

        {/* Selected-node detail panel */}
        {selectedNode && (
          <Paper elevation={6} sx={{ position: "absolute", top: 12, right: 12, width: 320, p: 2, bgcolor: "#141a2b", color: "#e6ebff", zIndex: 6 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Chip size="small" label={selectedNode.kind} sx={{ mr: 1, bgcolor: selectedNode.kind === "file" ? "#1c3a73" : "#1d4d2a", color: "#fff" }} />
              <Typography variant="subtitle2" sx={{ wordBreak: "break-all" }}>{selectedNode.label}</Typography>
            </Box>
            {selectedNode.filePath && (
              <Typography variant="body2" sx={{ color: "#8893b5", mb: 1, wordBreak: "break-all" }}>
                {selectedNode.filePath}{selectedNode.lineNumber ? `:${selectedNode.lineNumber}` : ""}
              </Typography>
            )}
            {selectedNode.kind === "file" && selectedNode.methodCount !== undefined && (
              <Typography variant="body2" sx={{ color: "#8893b5", mb: 1 }}>{selectedNode.methodCount} functions</Typography>
            )}
            <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  if (selectedNode.kind === "file") {
                    // Focus: hide the file graph and fill the view with ONLY this
                    // file's functions.
                    if (selectedNode.fileId !== undefined && selectedNode.filePath) {
                      dispatch(enterNetworkFocus(selectedNode.filePath));
                      actions.expandNetworkFile({ repoId, fileId: selectedNode.fileId, filePath: selectedNode.filePath });
                    }
                  } else if (selectedNode.methodId !== undefined) {
                    actions.expandNetworkFunction({ repoId, methodId: selectedNode.methodId });
                  }
                }}
              >
                {selectedNode.kind === "file" ? "Expand functions" : "Expand callees"}
              </Button>
              {selectedNode.kind === "function" && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() =>
                    selectedNode.methodId !== undefined &&
                    actions.expandNetworkCallers({ repoId, methodId: selectedNode.methodId })
                  }
                >
                  Expand callers
                </Button>
              )}
              {selectedNode.kind === "function" && (
                <Tooltip title="Explode into all transitive dependencies (across files)">
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    onClick={() =>
                      selectedNode.methodId !== undefined &&
                      actions.expandNetworkSlice({ repoId, methodId: selectedNode.methodId })
                    }
                  >
                    Program slice
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Show code in a popup">
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!selectedNode.filePath}
                    startIcon={<CodeIcon />}
                    onClick={() => showCode(selectedNode)}
                  >
                    Show code
                  </Button>
                </span>
              </Tooltip>
              {selectedNode.kind === "function" && selectedNode.methodId !== undefined && (
                <Tooltip title="Designate this function as a feature — its call flow is spidered out in FEATURE mode, and it's drift-tracked">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={markedMethods.has(selectedNode.methodId)}
                      onClick={() => markAsFeature(selectedNode.methodId as number)}
                      sx={{ color: "#e0a82e", borderColor: "#7a5a18", textTransform: "none" }}
                    >
                      {markedMethods.has(selectedNode.methodId) ? "Feature ✓" : "Mark as feature"}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Paper>
        )}
        {/* Live indexing feed — where the worker is right now */}
        {showFeed && (
          <Paper elevation={6} sx={{ position: "absolute", left: 320, bottom: 12, width: 360, p: 1.5, bgcolor: "#141a2b", color: "#e6ebff", zIndex: 6 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", mr: 1, bgcolor: workerActive ? "#3ddc84" : workerStalled ? "#ff9800" : "#5a6584" }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {workerActive ? "Worker active" : workerStalled ? "Worker not responding" : "Worker queued"}
              </Typography>
              {phase && <Chip size="small" label={phase} sx={{ ml: 1, height: 18, bgcolor: "#23304f", color: "#cfe0ff" }} />}
            </Box>

            {phase === "parsing" && filesTotal > 0 ? (
              <>
                <LinearProgress variant="determinate" value={Math.min(100, (filesDone / filesTotal) * 100)} sx={{ my: 0.5 }} />
                <Typography variant="caption" sx={{ color: "#8893b5" }}>
                  Parsing {filesDone} / {filesTotal} files
                </Typography>
              </>
            ) : (
              <>
                <LinearProgress sx={{ my: 0.5 }} />
                <Typography variant="caption" sx={{ color: "#8893b5" }}>
                  {phase === "resolving"
                    ? "Resolving dependency edges…"
                    : phase === "counting"
                    ? "Computing dependency counts…"
                    : "Starting…"}
                </Typography>
              </>
            )}

            {feed.length > 0 && (
              <Box sx={{ mt: 1, maxHeight: 130, overflow: "hidden" }}>
                {feed.map((f, i) => (
                  <Typography
                    key={`${f}-${i}`}
                    variant="caption"
                    component="div"
                    sx={{ fontFamily: "monospace", color: i === 0 ? "#9fe6b8" : "#6b7693", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {f}
                  </Typography>
                ))}
              </Box>
            )}
          </Paper>
        )}
        {/* Bottom-right: file search (on top) + expanded files (below) */}
        <Box sx={{ position: "absolute", right: 12, bottom: 12, width: 320, display: "flex", flexDirection: "column", gap: 1, zIndex: 6 }}>
          {/* File-name search */}
          <Paper elevation={6} sx={{ bgcolor: "#141a2b", color: "#e6ebff", p: searchOpen ? 1.25 : 0.5, alignSelf: "flex-end", width: searchOpen ? "100%" : "auto" }}>
            {searchOpen ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <SearchIcon fontSize="small" sx={{ mr: 1, color: "#8893b5" }} />
                  <InputBase
                    autoFocus
                    placeholder={focusFile ? "Find function…" : "Find file…"}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ flex: 1, color: "#e6ebff", fontSize: 13 }}
                  />
                  <IconButton size="small" sx={{ color: "#8893b5" }} onClick={() => { setSearchOpen(false); setSearchQuery(""); }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                {searchQuery.trim() !== "" && (
                  <Box sx={{ maxHeight: 220, overflow: "auto", mt: 0.5 }}>
                    {searchResults.length === 0 ? (
                      <Typography variant="caption" sx={{ color: "#8893b5" }}>No files match.</Typography>
                    ) : (
                      searchResults.map((f) => (
                        <Box
                          key={f.id}
                          onClick={() => focusNode(f)}
                          sx={{ px: 0.5, py: 0.5, cursor: "pointer", borderRadius: 1, "&:hover": { bgcolor: "#1c2438" } }}
                        >
                          <Typography variant="body2" sx={{ fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {f.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            title={f.filePath ?? ""}
                            sx={{ color: "#6b7693", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >
                            {f.filePath}
                          </Typography>
                        </Box>
                      ))
                    )}
                  </Box>
                )}
              </>
            ) : (
              <Tooltip title={focusFile ? "Find a function" : "Find a file"}>
                <IconButton sx={{ color: "#cfd8ff" }} onClick={() => setSearchOpen(true)}>
                  <SearchIcon />
                </IconButton>
              </Tooltip>
            )}
          </Paper>

          {/* Expanded ("opened") files — collapse them back down */}
          {expandedFiles.length > 0 && (
            <Paper elevation={6} sx={{ width: "100%", maxHeight: 280, p: 1.5, bgcolor: "#141a2b", color: "#e6ebff", display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="subtitle2">Expanded files ({expandedFiles.length})</Typography>
                <Button size="small" sx={{ color: "#9fb4ff" }} onClick={() => dispatch(collapseAllNetwork())}>
                  Collapse all
                </Button>
              </Box>
              <Box sx={{ overflow: "auto" }}>
                {expandedFiles.map((f) => (
                  <Box key={f.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.25 }}>
                    <Typography
                      variant="body2"
                      title={f.filePath ?? ""}
                      sx={{ fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mr: 1 }}
                    >
                      {f.label}
                    </Typography>
                    <Tooltip title="Collapse this file">
                      <IconButton
                        size="small"
                        sx={{ color: "#ff9aa2" }}
                        onClick={() => f.fileId !== undefined && dispatch(collapseNetworkFile(f.fileId))}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </Box>
      </Box>

      {/* Right-click context menu */}
      <Menu
        open={ctxMenu !== null}
        onClose={() => setCtxMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={ctxMenu ? { top: ctxMenu.y, left: ctxMenu.x } : undefined}
        PaperProps={{ sx: { bgcolor: "#1a2236", color: "#e6ebff" } }}
      >
        <MenuItem onClick={() => ctxMenu && showCode(ctxMenu.node)}>
          <CodeIcon fontSize="small" style={{ marginRight: 8 }} /> Show code!
        </MenuItem>
        {ctxMenu?.node.kind === "function" && ctxMenu.node.methodId !== undefined && (
          <MenuItem
            disabled={markedMethods.has(ctxMenu.node.methodId)}
            onClick={() => { markAsFeature(ctxMenu.node.methodId as number); setCtxMenu(null); }}
          >
            <span style={{ marginRight: 8, color: "#e0a82e" }}>★</span>
            {markedMethods.has(ctxMenu.node.methodId) ? "Feature ✓" : "Mark as feature"}
          </MenuItem>
        )}
      </Menu>

      {/* "Show code" modal */}
      <Dialog
        open={codeOpen}
        onClose={() => dispatch(closeNetworkCode())}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: "#0e1422", color: "#e6ebff" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1, borderBottom: "1px solid #1f2940" }}>
          <CodeIcon sx={{ mr: 1, color: "#8ab4ff" }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {codeTitle}
            </Typography>
            {codeSubtitle && (
              <Typography variant="caption" sx={{ color: "#8893b5", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {codeSubtitle}
              </Typography>
            )}
          </Box>
          <IconButton size="small" sx={{ color: "#8893b5" }} onClick={() => dispatch(closeNetworkCode())}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ maxHeight: "70vh", overflow: "auto" }}>
          {codeStatus === "loading" ? (
            <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={22} /></Box>
          ) : codeStatus === "failed" ? (
            <Typography sx={{ p: 2, color: "#ffb4b4" }}>Could not load source.</Typography>
          ) : (
            <Box
              component="pre"
              sx={{ m: 0, p: 2, fontFamily: "ui-monospace, monospace", fontSize: 12.5, lineHeight: 1.5, whiteSpace: "pre", overflowX: "auto", color: "#dbe6ff" }}
            >
              {codeBody}
            </Box>
          )}
        </Box>
      </Dialog>
    </Dialog>
  );
}
